/**
 * CustomerProfile Component
 * Main orchestration component for customer profile page
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Button, Typography } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useCustomer } from '../../../hooks/useCustomers';
import { TruckTimeline } from '../timeline/TruckTimeline';
import { CustomerDetailsCard } from './components/CustomerDetailsCard';
import { TruckList } from './components/TruckList';
import { ServiceHistoryTable } from './components/ServiceHistoryTable';
import { CollapsibleSection } from './components/CollapsibleSection';
import { DeleteTruckDialog } from './components/DeleteTruckDialog';
import { useCustomerForm } from './hooks/useCustomerForm';
import { useTruckManagement } from './hooks/useTruckManagement';
import { useServiceHistory } from './hooks/useServiceHistory';
import { usePreloadServiceHistory } from './hooks/usePreloadServiceHistory';
import { useOCR } from './hooks/useOCR';

export const CustomerProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: customer, isLoading } = useCustomer(id || '');

  // Section collapse state
  const [sectionsExpanded, setSectionsExpanded] = useState({
    timeline: true,
    trucks: true,
    servicedToday: true,
  });

  const toggleSection = (section: keyof typeof sectionsExpanded) => {
    setSectionsExpanded((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Custom hooks for business logic
  const { customerData, handleCustomerFieldChange } = useCustomerForm(id, customer);

  const {
    trucks,
    setTrucks,
    deleteModalOpen,
    truckToDelete,
    handleTruckUpdate,
    handleRegistrationDateChange,
    handleAddTruck,
    handleDeleteClick,
    handleCancelDelete,
    handleConfirmDelete,
    handleVehicleSelected,
  } = useTruckManagement(id, customer);

  const {
    expandedTruckId,
    serviceHistoryLoading,
    handleToggleExpand,
    handleAddServiceEntry,
    handleUpdateServiceStatus,
    handleUpdateServiceDate,
    handleDeleteServiceEntry,
  } = useServiceHistory(trucks, setTrucks);

  const {
    ocrLoading,
    vinInputRefs,
    licenseInputRefs,
    handleVinCamera,
    handleLicenseCamera,
    handleImageCapture,
  } = useOCR(handleTruckUpdate);

  // Preload service history for all trucks on page load
  usePreloadServiceHistory(trucks, setTrucks);

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  if (!customer) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Typography>Customer not found</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Back Button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/customers')}
        sx={{ mb: 3 }}
      >
        Back to Customers
      </Button>

      {/* Customer Details Card */}
      <CustomerDetailsCard
        customer={customer}
        customerData={customerData}
        onFieldChange={handleCustomerFieldChange}
      />

      {/* Truck Timeline Visualization */}
      {trucks.length > 0 && (
        <CollapsibleSection
          title="Service Timeline"
          isExpanded={sectionsExpanded.timeline}
          onToggle={() => toggleSection('timeline')}
        >
          <TruckTimeline trucks={trucks} />
        </CollapsibleSection>
      )}

      {/* Trucks Section */}
      <CollapsibleSection
        title="Trucks"
        isExpanded={sectionsExpanded.trucks}
        onToggle={() => toggleSection('trucks')}
        defaultPadding={false}
      >
        <TruckList
          trucks={trucks}
          expandedTruckId={expandedTruckId}
          ocrLoading={ocrLoading}
          serviceHistoryLoading={serviceHistoryLoading}
          vinInputRefs={vinInputRefs}
          licenseInputRefs={licenseInputRefs}
          onAddTruck={handleAddTruck}
          onToggleExpand={handleToggleExpand}
          onTruckUpdate={handleTruckUpdate}
          onRegistrationDateChange={handleRegistrationDateChange}
          onDeleteClick={handleDeleteClick}
          onVinCamera={handleVinCamera}
          onLicenseCamera={handleLicenseCamera}
          onImageCapture={handleImageCapture}
          onAddServiceEntry={handleAddServiceEntry}
          onUpdateServiceStatus={handleUpdateServiceStatus}
          onUpdateServiceDate={handleUpdateServiceDate}
          onDeleteServiceEntry={handleDeleteServiceEntry}
          onVehicleSelected={handleVehicleSelected}
        />
      </CollapsibleSection>

      {/* Service History Section */}
      <CollapsibleSection
        title="Service History"
        isExpanded={sectionsExpanded.servicedToday}
        onToggle={() => toggleSection('servicedToday')}
        defaultPadding={false}
      >
        <ServiceHistoryTable trucks={trucks} customer={customer} />
      </CollapsibleSection>

      {/* Delete Confirmation Modal */}
      <DeleteTruckDialog
        open={deleteModalOpen}
        truck={truckToDelete}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
    </Container>
  );
};

export default CustomerProfile;
